import useTranslation from 'next-translate/useTranslation'
import BodyText from '@ancon/wildcat-ui/web/orderweb/BodyText'
import HandIcon from '@ancon/wildcat-ui/shared/icons/hand-click-outline.svg'
import { OrderFormat } from '@ancon/wildcat-types'

import useAppSelector from '../../../../store/hooks/useAppSelector'
import Card from '../../../layout/components/card/Card'
import {
  outletDetailsSelector,
  outletIsOutletDetailsPendingSelector,
} from '../../../outlet/store/outletSelector'
import { outletsSelectedOutletListItemSelector } from '../../store/outletsSelectors'
import localStorageUtils from '../../../app/utils/localStorageUtils'
import { clientContextCurrentUserCompanyNameSelector } from '../../../clientContext/store/clientContextSelectors'
import { preOrderSupportedOrderFormatsSelector } from '../../../preOrder/store/preOrderSelectors'
import CompanyTag from '../../../company/components/CompanyTag'

import styles from './NewOrderCard.module.scss'
import NewOrderOutletDetails from './NewOrderOutletDetails'
import NewOrderOutletDetailsSkeleton from './NewOrderOutletDetailsSkeleton'

type NewOrderCardProps = {
  isInModal?: boolean
  /** "New company order for <Company>" with company order formats */
  isCompanyOrder?: boolean
  onClickOrderFormat: (orderFormat: OrderFormat) => void
}

export default function NewOrderCard({
  isInModal,
  isCompanyOrder = false,
  onClickOrderFormat,
}: NewOrderCardProps) {
  const { t } = useTranslation('outlets')

  const outletDetails = useAppSelector(outletDetailsSelector)
  const outletListItem = useAppSelector(outletsSelectedOutletListItemSelector)
  const outletDetailsFetchPending = useAppSelector(
    outletIsOutletDetailsPendingSelector,
  )
  const companyName = useAppSelector(
    clientContextCurrentUserCompanyNameSelector,
  )
  const companyOrderFormats = useAppSelector(
    preOrderSupportedOrderFormatsSelector,
  )

  function renderContent() {
    const preSelectedOutletId = localStorageUtils.getItem<string>(
      'preSelectedOutletId',
    )

    if (preSelectedOutletId) {
      if (outletDetailsFetchPending) {
        return <NewOrderOutletDetailsSkeleton />
      }

      const isOutletDataReady = !!outletDetails && !!outletListItem

      if (isOutletDataReady) {
        return (
          <NewOrderOutletDetails
            isInModal={isInModal}
            outletDetails={outletDetails}
            companyOrderFormats={
              isCompanyOrder ? companyOrderFormats : undefined
            }
            onClickOrderFormat={onClickOrderFormat}
          />
        )
      }
    }

    return (
      <div className={styles.placeholderWrapper}>
        <HandIcon />
        <BodyText>{t('newOrderCard.selectYourRestaurant')}</BodyText>
      </div>
    )
  }

  const companyTitle = isCompanyOrder && companyName && (
    <div className={styles.companyTitle}>
      <CompanyTag>{t('companyNewOrder.tag')}</CompanyTag>
      <BodyText color="body-1">
        {t('companyNewOrder.title', { companyName })}
      </BodyText>
    </div>
  )

  if (isInModal) {
    return (
      <>
        {companyTitle}
        {renderContent()}
      </>
    )
  }

  return (
    <Card className={styles.container} tabIndex={0}>
      {companyTitle}
      {renderContent()}
    </Card>
  )
}
